import { TableSqlBuilderHelper } from 'src/app/components/table-dashboard-item/sql-builder-helper';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import { multipleSelectComponentCompileSQLTokens } from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { sqlDateTrunc } from 'src/app/utils/sql';

export class BiStgPlayerBiVizDimsDatasource {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_stg',
        tableId: 'player_biviz_dims',
        columns: {
            FIREBASE_FIRST_OPEN_DATE: {
                columnId: 'FIREBASE_FIRST_OPEN_DATE',
                columnSQL: 'revenue_sessions.first_session_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'revenue_sessions.first_session_date',
                        state
                    );
                },
            },
            FIREBASE_APP_STORE: {
                columnId: 'FIREBASE_APP_STORE',
                columnSQL: 'revenue_sessions.first_store',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'revenue_sessions.first_store',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_TIER: {
                columnId: 'FIREBASE_COUNTRY_TIER',
                columnSQL: 'geo.first_geo.country_tier_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'geo.first_geo.country_tier_label',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY: {
                columnId: 'FIREBASE_COUNTRY',
                columnSQL: 'geo.first_geo.unified_country',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'geo.first_geo.unified_country',
                        state
                    );
                },
            },
            ATTRIBUTION_TRAFFIC_SOURCE: {
                columnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                columnSQL: 'attribution.unified_traffic',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_traffic',
                        state
                    );
                },
            },
            ATTRIBUTION_NETWORK_NAME: {
                columnId: 'ATTRIBUTION_NETWORK_NAME',
                columnSQL: 'attribution.unified_network',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_network',
                        state
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_NAME: {
                columnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                columnSQL: 'attribution.unified_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_campaign',
                        state
                    );
                },
            },
            ATTRIBUTION_ADSET: {
                columnId: 'ATTRIBUTION_ADSET',
                columnSQL: 'attribution.unified_adset',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_adset',
                        state
                    );
                },
            },
            ATTRIBUTION_AD: {
                columnId: 'ATTRIBUTION_AD',
                columnSQL: 'attribution.unified_ad',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_ad',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_LENGTH_S: {
                columnId: 'ATTRIBUTION_AD_LENGTH_S',
                columnSQL: 'attribution.unified_ad_dim.length_s',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_ad_dim.length_s',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_SIZE: {
                columnId: 'ATTRIBUTION_AD_SIZE',
                columnSQL: 'attribution.unified_ad_dim.size',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_ad_dim.size',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_ASPECT_RATIO: {
                columnId: 'ATTRIBUTION_AD_ASPECT_RATIO',
                columnSQL: 'attribution.unified_ad_dim.aspect_ratio',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_ad_dim.aspect_ratio',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_TYPE: {
                columnId: 'ATTRIBUTION_AD_TYPE',
                columnSQL: 'attribution.unified_ad_dim.type',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_ad_dim.type',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_THEME: {
                columnId: 'ATTRIBUTION_AD_THEME',
                columnSQL: 'attribution.unified_ad_dim.theme',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_ad_dim.theme',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_VERSION: {
                columnId: 'ATTRIBUTION_AD_VERSION',
                columnSQL: 'attribution.unified_ad_dim.version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'attribution.unified_ad_dim.version',
                        state
                    );
                },
            },
        },
    };

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const dateTrunc = queryContextSettings.dateAggregation
            ? sqlDateTrunc('date', queryContextSettings.dateAggregation)
            : `date`;

        const sqlTokensArray: {
            id: DatasourceColumnId;
            compiledSqlTokens: Partial<ICompiledSQLTokens>;
        }[] = supportedDatasourceColumns.map((dsCol) => {
            return {
                id: dsCol.id,
                compiledSqlTokens: this.sqlMap.columns[dsCol.id]!
                    .compileSQLTokens!(dsCol.state, options),
            };
        });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        // create splitSelect in the form: dsCol.columnSQL AS dsCol.id
        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);

        const splitSelectWithMeta: {
            id: DatasourceColumnId;
            compiledSqlTokens: Partial<ICompiledSQLTokens>;
        }[] = supportedDatasourceColumns.map((dsCol) => {
            return {
                id: dsCol.id,
                compiledSqlTokens: this.sqlMap.columns[dsCol.id]!
                    .compileSQLTokens!(dsCol.state, options),
            };
        });
        const splitSelectAsSeparateColumns = (suffix: string = ''): string => {
            const sql =
                TableSqlBuilderHelper.getSplitSelect(splitSelectWithMeta);
            return sql == '' ? '' : sql + suffix;
        };
        const splitSelectDatasourceColumnsIds = (options: {
            suffix?: string;
            alias?: string;
        }): string => {
            options.suffix = options.suffix || '';
            const sql = TableSqlBuilderHelper.getSplitSelect(
                splitSelectWithMeta,
                true,
                options.alias
            );
            return sql == '' ? '' : sql + options.suffix;
        };

        // create splitGroupBy in the form: dsCol.id
        const splitGroup = splitSelect ? 'split_group' : '';
        const splitGroupBy = splitSelect ? 'split_group, ' : '';

        // CUSTOM FILTER FOR THIS TABLE
        // sqlTokens.where.push(`validation_flag IS NULL`);

        // unnest app_version_activity if app_version is present in splits or filters
        // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        const join: string = sqlTokens.join.join('\n');

        return {
            dateTrunc,
            splitSelect,
            splitSelectAsSeparateColumns,
            splitSelectDatasourceColumnsIds,
            splitGroup,
            splitGroupBy,
            where,
            join,
        };
    }
}
